import {FilterType, Gender} from "./product";
import {NY_CountryService} from "../services/country-service";
import {filterService} from "../services/filter-service";
import {config} from "../config";

export class ProductFilter {
  public name: string;
  public value: any;
  public type: FilterType;
  public image_key: string;
  public selected: boolean;
  public expanded: boolean;
  public filters: ProductFilter[];
  public translation?: string;

  constructor(filter: any) {
    this.name = filter.name;
    this.value = filter.value;
    this.type = filter.type;
    this.image_key = filter.image_key;
    this.selected = filter.selected;

    this.filters = [];
    if (filter.filters) {
      filter.filters.forEach((filter) => {
        this.filters.push(new ProductFilter(filter))
      });
    }
  }

  hasSubFilter():boolean {
    return this.filters && this.filters.length > 0;
  }
}

export class FilterMap {
  public gender:      Gender          = Gender.Female;
  public country:     string          = 'de';
  public brands:      ProductFilter[] = [];
  public categories:  ProductFilter[] = [];
  public custom:      ProductFilter[] = [];
  public colors:      ProductFilter[] = [];
  public likes:       ProductFilter[] = [];
  public collections: ProductFilter[] = [];
  public editorials:  ProductFilter[] = [];

  public selected:   ProductFilter[] = [];

  constructor(gender: Gender, country: string) {
    this.gender = gender;
    this.country = country;
    if(!NY_CountryService.isRestricted(this.country)) {
      this.likes.push(new ProductFilter({name: 'like', value: 'true', type: FilterType.Like}));
    }
  }

  selectedFiltersWithoutFilterType(filterType: FilterType): ProductFilter[] {
    return this.selected.filter(filter => filter.type !== filterType);
  }

  clone():FilterMap {
    const newFilterMap = new FilterMap(this.gender, this.country);

    for (const productFilter of this.custom) {
      newFilterMap.custom.push(new ProductFilter(productFilter))
    }

    for (const productFilter of this.brands) {
      newFilterMap.brands.push(new ProductFilter(productFilter))
    }

    for (const productFilter of this.categories) {
      newFilterMap.categories.push(new ProductFilter(productFilter))
    }

    for (const productFilter of this.colors) {
      newFilterMap.colors.push(new ProductFilter(productFilter))
    }

    for (const productFilter of this.likes) {
      newFilterMap.likes.push(new ProductFilter(productFilter))
    }

    for (const productFilter of this.collections) {
      newFilterMap.collections.push(new ProductFilter(productFilter))
    }

    for (const productFilter of this.editorials) {
      newFilterMap.editorials.push(new ProductFilter(productFilter))
    }

    return newFilterMap;
  }

  applyFilters(values: any) {
    if (values.brands) {
      this.brands = values.brands.map((it) => new ProductFilter(it));
    }
    if (values.categories) {
      this.categories = values.categories.map((it) => new ProductFilter(it));
    }
    if (values.custom) {
      this.custom = values.custom.map((it) => new ProductFilter(it));
    }
    if (values.colors) {
      this.colors = values.colors.map((it) => new ProductFilter(it));
    }
    if (values.collections) {
      this.collections = values.collections.map((it) => new ProductFilter(it));
      this.editorials = values.collections.filter(collection => collection.type === FilterType.Editorial).map((it) => new ProductFilter(it));
    }
    this.updateSelectedFilters();
  }

  // noinspection JSMethodCanBeStatic
  clearFilters(filters: ProductFilter[]) {
    for (const category of filters) {
      category.expanded = false;
      category.selected = false;
      if (category.hasSubFilter()) {
        for (const subFilter of category.filters) {
          subFilter.expanded = false;
          subFilter.selected = false;
        }
      }
    }
  }

  getCleanFilterUrl() {
    const filters = this.clone()
    filters.clearAll()
    return `/?${filterService.createUrlSearchParams(filters)}`
  }

  clearAll() {
    this.clearFilters(this.custom);
    this.clearFilters(this.brands);
    this.clearFilters(this.categories);
    this.clearFilters(this.colors);
    this.clearFilters(this.likes);
    this.clearFilters(this.collections);
    this.clearFilters(this.editorials);
    this.selected = [];
  }

  updateSelectedFilters() {
    this.selected = [];
    this.selected.push(...this.getSelected(this.custom));
    this.selected.push(...this.getSelected(this.brands));
    this.selected.push(...this.getSelected(this.categories));
    this.selected.push(...this.getSelected(this.colors));
    this.selected.push(...this.getSelected(this.likes));
    this.selected.push(...this.getSelected(this.collections));
    this.selected.push(...this.getSelected(this.editorials));
  }

  private getFilterForType(type: FilterType): ProductFilter[] {
    switch (type) {
      case FilterType.Custom: return this.custom;
      case FilterType.Brand: return this.brands;
      case FilterType.Category: return this.categories;
      case FilterType.Color: return this.colors;
      case FilterType.Like: return this.likes;
      case FilterType.Collection: return this.collections;
      case FilterType.Editorial: return this.editorials;
      default: return []
    }
  }

  toggleFilterUrl(filter: ProductFilter): string {
    const map = this.clone()
    const productFilters = map.getFilterForType(filter.type)
    let fil: ProductFilter = null;

    for (const productFilter of productFilters) {
      if(productFilter.name == filter.name) fil = productFilter;

      if(productFilter.hasSubFilter()) {
        for (const subFilter of productFilter.filters) {
          if(subFilter.name == filter.name) fil = subFilter;
        }
      }
    }

    if(fil) {
      map.toggleFilter(fil)
    }

    return config.url(`/?${filterService.createUrlSearchParams(map)}`);
  }

  toggleFilter(filter: ProductFilter): void {
    if (filter.type === FilterType.Brand || filter.type === FilterType.Custom || filter.type === FilterType.Like || filter.type === FilterType.Collection || filter.type === FilterType.Editorial) {
      if (filter.selected) {
        filter.selected = false;
      } else {
        // clear all other filters and select this one
        this.clearAll();
        filter.selected = true;
      }
    } else if (filter.type === FilterType.Category) {
      if (this.likes.length > 0) {
        this.likes[0].selected = false;
      }
      this.clearFilters(this.collections);

      const previousSelected: boolean = filter.selected;
      const previousExpended: boolean = filter.expanded;
      this.clearFilters(this.categories);


      if (filter.filters && filter.hasSubFilter()) {
        //main category ausgewählt

        if (previousExpended && !previousSelected) {
          filter.expanded = true;
          filter.selected = true;
        } else {
          filter.expanded = !previousExpended;
          filter.selected = !previousSelected;
        }
      } else {
        // sub category ausgewählt
        const parent: ProductFilter = this.findParent(filter, this.categories);
        if (parent) {
          parent.selected = false;
          parent.expanded = !previousSelected;
          filter.selected = !previousSelected;
        }
      }

    } else if (filter.type === FilterType.Color) {
      if (this.likes.length > 0) {
        this.likes[0].selected = false;
      }
      this.clearFilters(this.collections);
      this.clearFilters(this.editorials);
      filter.selected = !filter.selected;
    }
    this.updateSelectedFilters();
  };

  selectFilter(name: string, filters: ProductFilter[] ): boolean {
    for (const filter of filters) {
      if (filter.type === FilterType.Collection ||filter.type === FilterType.Editorial) {
        if (filter.value.toString() === name) {
          filter.selected = true;
        }
      }
      else if (filter.name.toLowerCase() == name.toLowerCase()) {
        filter.selected = true;
        if (filter.hasSubFilter()) {
          filter.expanded = true;
        }
        return true;
      }
      if (filter.hasSubFilter()) {
        for (const subFilter of filter.filters) {
          if (subFilter.name.toLowerCase() == name.toLowerCase()) {
            subFilter.selected = true;
            filter.expanded = true;
            return true;
          }
        }
      }
    }
    return false
  };

  // noinspection JSMethodCanBeStatic
  private findParent(filter: ProductFilter, categories: ProductFilter[]): ProductFilter {
    for (const category of categories) {
      if (category.hasSubFilter()) {
        for (const subFilter of category.filters) {
          if (subFilter.value === filter.value) {
            return category;
          }
        }
      }
    }
    return null
  }

  getQueryFilters(): any {
    const filters:any = {};
    filters.country = this.country;
    filters.gender = this.gender;

    filters.brand = this.createQueryString(this.brands);
    filters.color = this.createQueryString(this.colors);
    filters.web_category = this.createQueryString(this.categories);
    filters.likes = this.createQueryString(this.likes);
    filters.collections = this.createQueryString(this.collections);
    filters.editorials = this.createQueryString(this.editorials);

    for (const customFilter of this.custom) {
      if (customFilter.selected) {
        filters[customFilter.name] = true;
      }
    }

    return filters;
  }

  // noinspection JSMethodCanBeStatic
  createQueryString(filters: ProductFilter[]):string {
    const result: string [] = [];
    for (const filter of filters) {
      if (filter.selected) {
        result.push(filter.name);
        // select all sub filters
        if (filter.hasSubFilter()) {
          for (const subFilter of filter.filters) {
            result.push(subFilter.name);
          }
        }
      } else {
        // check if sub-filters are selected
        if (filter.hasSubFilter()) {
          for (const subFilter of filter.filters) {
            if (subFilter.selected) {
              result.push(subFilter.name);
            }
          }
        }
      }

    }
    return result.join(",")
  }

  // noinspection JSMethodCanBeStatic
  private getSelected(filters: ProductFilter[]):ProductFilter[] {
    const result:ProductFilter[] = [];
    for (const filter of filters) {
      if (filter.selected) {
        result.push(filter);
      }
      if (filter.hasSubFilter()) {
        for (const subFilter of filter.filters) {
          if (subFilter.selected) {
            result.push(subFilter);
          }
        }
      }
    }
    return result
  }
}
